import { BaseApiService } from './base';
import { API_ENDPOINTS } from '../../config/api';

/**
 * Service pour la gestion des projets et équipes
 */
class ProjectService extends BaseApiService {
  // ============================================================================
  // PROJETS
  // ============================================================================

  /**
   * Récupère tous les projets
   */
  async getProjects(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.PROJECTS);
    } catch (error) {
      console.error('Erreur récupération projets:', error);
      return [];
    }
  }

  /**
   * Récupère un projet par ID
   */
  async getProject(id: string): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}${id}/`);
  }

  /**
   * Crée un nouveau projet
   */
  async createProject(projectData: {
    name: string;
    description?: string;
    color?: string;
    status?: string;
    priority?: string;
    start_date?: string;
    end_date?: string;
    team?: number;
    members?: number[];
  }): Promise<any> {
    return this.request(API_ENDPOINTS.PROJECTS, {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  /**
   * Met à jour un projet
   */
  async updateProject(id: string, projectData: Partial<any>): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}${id}/`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }

  /**
   * Supprime un projet
   */
  async deleteProject(id: string): Promise<void> {
    await this.request(`${API_ENDPOINTS.PROJECTS}${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Récupère les statistiques des projets
   */
  async getProjectStats(): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}stats/`);
  }

  /**
   * Récupère mes projets
   */
  async getMyProjects(): Promise<any[]> {
    try {
      const response: any = await this.request(`${API_ENDPOINTS.PROJECTS}my_projects/`);
      return response || [];
    } catch (error) {
      console.error('Erreur récupération mes projets:', error);
      return [];
    }
  }

  /**
   * Ajoute un membre à un projet
   */
  async addProjectMember(projectId: string, userId: number, role: string = 'member'): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}${projectId}/add_member/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role }),
    });
  }

  /**
   * Retire un membre d'un projet
   */
  async removeProjectMember(projectId: string, userId: number): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}${projectId}/remove_member/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  /**
   * Récupère les documents d'un projet
   */
  async getProjectDocuments(projectId: string): Promise<any[]> {
    try {
      const response: any = await this.request(`${API_ENDPOINTS.PROJECTS}${projectId}/documents/`);
      return response || [];
    } catch (error) {
      console.error('Erreur récupération documents projet:', error);
      return [];
    }
  }

  /**
   * Ajoute un document à un projet
   */
  async addProjectDocument(projectId: string, documentId: number): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}${projectId}/add_document/`, {
      method: 'POST',
      body: JSON.stringify({ document_id: documentId }),
    });
  }

  /**
   * Retire un document d'un projet
   */
  async removeProjectDocument(projectId: string, documentId: number): Promise<any> {
    return this.request(`${API_ENDPOINTS.PROJECTS}${projectId}/remove_document/`, {
      method: 'POST',
      body: JSON.stringify({ document_id: documentId }),
    });
  }

  // ============================================================================
  // ÉQUIPES
  // ============================================================================

  /**
   * Récupère toutes les équipes
   */
  async getTeams(): Promise<any[]> {
    try {
      // Liste paginee : on suit les pages, sinon elle se tronque a vingt.
      return await this.requestList<any>(API_ENDPOINTS.TEAMS);
    } catch (error) {
      console.error('Erreur récupération équipes:', error);
      return [];
    }
  }

  /**
   * Récupère une équipe par ID
   */
  async getTeam(id: string): Promise<any> {
    return this.request(`${API_ENDPOINTS.TEAMS}${id}/`);
  }

  /**
   * Crée une nouvelle équipe
   */
  async createTeam(teamData: {
    name: string;
    description?: string;
    members?: number[];
  }): Promise<any> {
    return this.request(API_ENDPOINTS.TEAMS, {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  }

  /**
   * Met à jour une équipe
   */
  async updateTeam(id: string, teamData: Partial<any>): Promise<any> {
    return this.request(`${API_ENDPOINTS.TEAMS}${id}/`, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
  }

  /**
   * Supprime une équipe
   */
  async deleteTeam(id: string): Promise<void> {
    await this.request(`${API_ENDPOINTS.TEAMS}${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Ajoute un membre à une équipe
   */
  async addTeamMember(teamId: string, userId: number): Promise<any> {
    return this.request(`${API_ENDPOINTS.TEAMS}${teamId}/add_member/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  /**
   * Retire un membre d'une équipe
   */
  async removeTeamMember(teamId: string, userId: number): Promise<any> {
    return this.request(`${API_ENDPOINTS.TEAMS}${teamId}/remove_member/`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }
}

export const projectService = new ProjectService();
export default projectService;

